import React, { useState, useEffect, useRef } from 'react';
import { getProfile, deleteProfil, setAktivesProfil } from '../api';
import { useConfirm } from './ConfirmDialog';
import ProfilErstellen from './ProfilErstellen';
import ProfilBearbeiten from './ProfilBearbeiten';
import './ProfilAuswahl.css';

// ── Farbpalette ─────────────────────────────────────────────────
const PALETTE = [
  { bg: '#FF6600', fg: '#FFFFFF' }, { bg: '#004D4D', fg: '#FFBC00' },
  { bg: '#006064', fg: '#FDF5E6' }, { bg: '#7c3aed', fg: '#fff' },
  { bg: '#b91c1c', fg: '#fff' }, { bg: '#065f46', fg: '#6ee7b7' },
  { bg: '#92400e', fg: '#fde68a' }, { bg: '#1e3a5f', fg: '#93c5fd' },
];

function profilFarbe(displayName) {
  let h = 0;
  for (let i = 0; i < displayName.length; i++) h = displayName.charCodeAt(i) + h * 31;
  return PALETTE[Math.abs(h) % PALETTE.length];
}

function profilInitials(displayName) {
  return displayName.trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase();
}

// ── ProfilKarte ─────────────────────────────────────────────────
function ProfilKarte({ p, isAktiv, onOeffnen, onLoeschen, onAktivieren }) {
  const [menuOffen, setMenuOffen] = useState(false);
  const menuRef = useRef(null);

  const displayName = p.vorname && p.nachname
    ? `${p.vorname} ${p.nachname}`
    : (p.name || 'Unbekannt');
  const { bg, fg } = profilFarbe(displayName);
  const ini = profilInitials(displayName);

  useEffect(() => {
    if (!menuOffen) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOffen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOffen]);

  return (
    <div className={`np-card${isAktiv ? ' np-card--aktiv' : ''}`} style={{ width: 110 }}>
      {/* Avatar — Klick öffnet Profil direkt */}
      <div
        className="np-avatar"
        style={{ background: bg, color: fg, width: 98, height: 98, cursor: 'pointer' }}
        onClick={() => onOeffnen(p.dateiname)}
      >
        <span className="np-initials" style={{ fontSize: 30 }}>{ini}</span>
      </div>

      {isAktiv && <span className="np-aktiv-badge">Aktiv</span>}
      <span className="np-name">{displayName}</span>

      {/* 3-Punkte Kontextmenü */}
      <div className="np-ctx-wrap" ref={menuRef}>
        <button
          className="np-ctx-btn"
          title="Optionen"
          onClick={(e) => { e.stopPropagation(); setMenuOffen(v => !v); }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <circle cx="5"  cy="12" r="2" fill="currentColor"/>
            <circle cx="12" cy="12" r="2" fill="currentColor"/>
            <circle cx="19" cy="12" r="2" fill="currentColor"/>
          </svg>
        </button>
        {menuOffen && (
          <div className="np-ctx-menu">
            {!isAktiv && (
              <button
                className="np-ctx-item"
                onClick={() => { setMenuOffen(false); onAktivieren(p.dateiname); }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                  <polyline points="20 6 9 17 4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Profil aktivieren
              </button>
            )}
            <button
              className="np-ctx-item np-ctx-item--del"
              onClick={() => { setMenuOffen(false); onLoeschen(p.dateiname, displayName); }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                <path d="M21 5.98C17.67 5.65 14.32 5.48 10.98 5.48C9 5.48 7.02 5.58 5.04 5.78L3 5.98" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M8.5 4.97L8.72 3.66C8.88 2.71 9 2 10.69 2H13.31C15 2 15.13 2.75 15.28 3.67L15.5 4.97" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                <path d="M18.85 9.14L18.2 19.21C18.09 20.78 18 22 15.21 22H8.79C6 22 5.91 20.78 5.8 19.21L5.15 9.14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              Löschen
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── NameBestaetigungModal ────────────────────────────────────────
function NameBestaetigungModal({ name, onBestaetigt, onAbbrechen }) {
  const [eingabe, setEingabe] = useState('');
  return (
    <div className="confirm-overlay" onClick={onAbbrechen}>
      <div className="confirm-dialog" onClick={e => e.stopPropagation()} style={{ maxWidth: 400 }}>
        <div className="confirm-icon-wrap confirm-icon-wrap--danger">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
          </svg>
        </div>
        <p className="confirm-message">
          Tippe den vollständigen Namen des Profils ein, um es endgültig zu löschen:
        </p>
        <p style={{ fontFamily: 'monospace', fontWeight: 700, textAlign: 'center', margin: '0 0 14px', fontSize: 14 }}>
          „{name}"
        </p>
        <input
          autoFocus
          value={eingabe}
          onChange={e => setEingabe(e.target.value)}
          placeholder={name}
          style={{
            width: '100%', boxSizing: 'border-box', padding: '10px 14px',
            borderRadius: 10, border: '1.5px solid var(--border, #ddd)',
            fontSize: 14, marginBottom: 18, outline: 'none',
          }}
          onKeyDown={e => { if (e.key === 'Enter' && eingabe.trim() === name.trim()) onBestaetigt(); }}
        />
        <div className="confirm-actions">
          <button className="confirm-btn confirm-btn--cancel" onClick={onAbbrechen}>Abbrechen</button>
          <button
            className="confirm-btn confirm-btn--danger"
            disabled={eingabe.trim() !== name.trim()}
            onClick={onBestaetigt}
          >
            Endgültig löschen
          </button>
        </div>
      </div>
    </div>
  );
}

// ── ProfilModal ─────────────────────────────────────────────────
function ProfilModal({ onSchliessen, onProfilGespeichert, nutzer, initialDateiname, initialAnsicht, initialSektion, initialSektionWert }) {
  const confirm = useConfirm();
  const [ansicht, setAnsicht] = useState(initialDateiname ? 'bearbeiten' : (initialAnsicht ?? 'liste'));
  const [profile, setProfile] = useState([]);
  const [ausgewaehlt, setAusgewaehlt] = useState(initialDateiname ?? null);
  const [fehler, setFehler] = useState('');
  const [loeschBestaetigung, setLoeschBestaetigung] = useState(null); // { dateiname, name }

  const ladeProfile = () => {
    setFehler('');
    getProfile()
      .then((r) => {
        setProfile(r.data);
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          setFehler('Deine Sitzung konnte noch nicht für Profil-Daten genutzt werden. Bitte lade die Seite neu oder melde dich erneut an.');
        } else {
          setFehler('Profile konnten nicht geladen werden.');
        }
      });
  };

  useEffect(() => {
    ladeProfile();
  }, []);

  useEffect(() => {
    if (ansicht === 'liste') ladeProfile();
  }, [ansicht]);

  const onOeffnen = (dateiname) => {
    setAusgewaehlt(dateiname);
    setAnsicht('bearbeiten');
  };

  const onAktivieren = async (dateiname) => {
    try {
      await setAktivesProfil(dateiname);
      ladeProfile();
      onProfilGespeichert?.(dateiname);
    } catch {
      setFehler('Profil konnte nicht aktiviert werden.');
    }
  };

  const onLoeschen = async (dateiname, name) => {
    if (!await confirm(`Profil „${name}" wirklich löschen?`)) return;
    setLoeschBestaetigung({ dateiname, name });
  };

  const onLoeschenBestaetigt = async () => {
    const { dateiname } = loeschBestaetigung;
    setLoeschBestaetigung(null);
    try {
      await deleteProfil(dateiname);
      const aktualisiert = profile.filter((p) => p.dateiname !== dateiname);
      setProfile(aktualisiert);
      if (ansicht === 'bearbeiten' && ausgewaehlt === dateiname) {
        setAusgewaehlt(null);
        setAnsicht('liste');
      }
    } catch {
      setFehler('Profil konnte nicht gelöscht werden.');
    }
  };

  // Aktives Profil aus Backend-Feld; Fallback auf erstes
  const aktivDateiname = profile.find(p => p.aktiv)?.dateiname ?? profile[0]?.dateiname ?? null;

  return (
    <div className="modal-overlay" onClick={onSchliessen}>
      <div className="modal-inhalt" style={initialSektion ? { animation: 'none' } : undefined} onClick={(e) => e.stopPropagation()}>
        <button className="modal-schliessen" onClick={onSchliessen}>✕</button>
        <div className="modal-scroll">

        {ansicht === 'liste' && (
          <>
            <h2 style={{ fontFamily: 'var(--font-head)', fontSize: 20, fontWeight: 700, color: 'var(--txt-primary)', margin: '0 0 28px', letterSpacing: '-0.02em' }}>
              Profile verwalten
            </h2>
            {fehler && (
              <div style={{
                margin: '0 0 18px',
                padding: '12px 14px',
                borderRadius: 12,
                background: 'rgba(220,38,38,0.08)',
                color: '#b91c1c',
                fontSize: 13,
                fontWeight: 700,
              }}>
                {fehler}
              </div>
            )}
            <div className="np-grid np-modal" style={{ gap: 20, justifyContent: 'center' }}>
              {profile.map((p) => (
                <ProfilKarte
                  key={p.dateiname}
                  p={p}
                  isAktiv={p.dateiname === aktivDateiname}
                  onOeffnen={onOeffnen}
                  onLoeschen={onLoeschen}
                  onAktivieren={onAktivieren}
                />
              ))}

              {/* Neues Profil */}
              <div className="np-card" style={{ width: 110 }} onClick={() => setAnsicht('erstellen')}>
                <div className="np-avatar np-avatar--new" style={{ width: 98, height: 98 }}>
                  <span className="np-plus" style={{ fontSize: 38 }}>+</span>
                </div>
                <span className="np-name">Neues Profil</span>
              </div>
            </div>
          </>
        )}

        {ansicht === 'erstellen' && (
          <ProfilErstellen
            onZurueck={() => setAnsicht('liste')}
            onErstellt={(dateiname) => {
              ladeProfile();
              setAusgewaehlt(dateiname);
              setAnsicht('bearbeiten');
            }}
          />
        )}

        {ansicht === 'bearbeiten' && (
          <ProfilBearbeiten
            dateiname={ausgewaehlt}
            initialSektion={initialSektion}
            initialSektionWert={initialSektionWert}
            onZurueck={() => setAnsicht('liste')}
            onGespeichert={onProfilGespeichert}
            onLoeschen={async () => {
              const p = profile.find(x => x.dateiname === ausgewaehlt);
              const name = p ? (p.vorname && p.nachname ? `${p.vorname} ${p.nachname}` : p.name) : ausgewaehlt;
              await onLoeschen(ausgewaehlt, name || ausgewaehlt);
            }}
          />
        )}
        </div>
      </div>
      {loeschBestaetigung && (
        <NameBestaetigungModal
          name={loeschBestaetigung.name}
          onBestaetigt={onLoeschenBestaetigt}
          onAbbrechen={() => setLoeschBestaetigung(null)}
        />
      )}
    </div>
  );
}

export default ProfilModal;
